import { VehicleOption, MaxiTaxiLookUpType, MaxiTaxiSeaterInfoPayload } from "../Redux/ConditionEntities";
import React, { FC, useState, useEffect, useRef } from "react";
import { getContentUrl, ContentURL } from "../../Utils/ContentURL";
import { ApplicationState } from "../../../appState";
import { connect } from "react-redux";
import "./ConditionInfoView.scss";
import { Dispatch } from "../../Dispatch";
import { GetFareDisplayText } from "../../Fare/FareHelper";
import MaxiSeaterSelector from "../../../widgets/maxi-seater-selector/maxi-seater-selector";
import { IsFixedFareAvailable, IsValidFareDataAvailable } from "../ShouldDisplayFare";
import { ValidateServicesForSatss } from "../ConditionValidation";
import { BookingWidgetModeKind } from "../../BookingTemplate/BookingTemplateEntities";
import { BrandedImage, GetBrandedUrl } from "../../Utils/BrandedContentUrls";
import { OptionalUI } from "../../Booking/OptionalParts/OptionalUI";

interface PropsFromStore {
    ActiveService: VehicleOption;
    ConditionList: VehicleOption[];
    MaxiTaxiLookUp: MaxiTaxiLookUpType;
    MaxiTaxiSeaterInfo: MaxiTaxiSeaterInfoPayload;
    SelectedCondition: VehicleOption;
    IsPriceGuaranteeSelected: boolean;
    BookingWidgetMode: BookingWidgetModeKind;
    IsValidFareDataAvailable: boolean;
    IsFixedFareAvailable: boolean;

    /** Whether the PG Fee is supported by the selected payment method */
    DoesPgFeeApply: boolean;
}

/** Component to display more info about selected service. Opens as a full page popup. */
const ConditionInfoView: FC<PropsFromStore> = (props) => {

    const [NumberOfSeats, setNumberOfSeats] = useState(0);
    const backButtonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        backButtonRef.current!.focus();
    }, []);

    useEffect(() => {
        let selectedMaxiTaxiSeaterNo = props.MaxiTaxiSeaterInfo.Average;

        if (props.ActiveService.Service.isMaxiTaxi && props.SelectedCondition.Service.isMaxiTaxi) {
            selectedMaxiTaxiSeaterNo = props.SelectedCondition.ApiVehicle?.MaxSeat!;
        }

        if (selectedMaxiTaxiSeaterNo !== 0) {
            setNumberOfSeats(selectedMaxiTaxiSeaterNo);
        }
    }, [props.MaxiTaxiSeaterInfo, props.ActiveService, props.SelectedCondition]);

    /** Close the popup. */
    function CloseInfoView() {
        Dispatch.Condition.HideServiceInfo();
    }

    /** Update store on click of select button and hide the popup. */
    function SelectCondition() {
        let selectedVehicle = props.ActiveService;
        if (selectedVehicle.Service.isMaxiTaxi) {
            const condition = props.MaxiTaxiLookUp[NumberOfSeats];
            if (condition) {
                selectedVehicle = {
                    ...selectedVehicle,
                    Service: { ...selectedVehicle.Service, short: condition.Description },
                    ApiVehicle: condition
                };
            }
        }

        Dispatch.Condition.SelectVehicle(selectedVehicle);
        Dispatch.Condition.CollapseVehicleSelectorUI();
        Dispatch.Condition.HideServiceInfo();
        ValidateServicesForSatss();
    }

    /** Set/Update number of passengers for Maxi Taxi. */
    function SetMaxiTaxiSeatNo(numberOfSeats: number) {
        setNumberOfSeats(numberOfSeats);
        
        // Update the selected condition in Redux immediately when seat count changes
        const condition = props.MaxiTaxiLookUp[numberOfSeats];
        if (condition) {
            const updatedVehicle = {
                ...props.ActiveService,
                Service: { ...props.ActiveService.Service, short: condition.Description },
                ApiVehicle: condition
            };
            Dispatch.Condition.SelectVehicle(updatedVehicle);
        }
    }

    const ActiveService = props.ActiveService;

    let fareConditionTitle = null;
    let fareConditionText = null;
    let fareAmount = "";
    let priceIconUrl = null;

    if (props.IsFixedFareAvailable && props.IsPriceGuaranteeSelected && ActiveService.FareDetails) {
        fareConditionTitle = "Fixed Price";
        fareConditionText = "The price of your fare is based on the metered taxi rates and is inclusive of all extras and fees if you pay by card or other electronic means. If you pay by cash, the price charged by the driver may differ.";
        fareAmount = GetFareDisplayText(ActiveService.FareDetails, true, props.DoesPgFeeApply);
        priceIconUrl = GetBrandedUrl(BrandedImage.LockIcon);
    }
    else {
        fareConditionTitle = "Fare Estimates";
        fareConditionText = "Prices given are an estimate only based on kilometres travelled. Actual fare may vary due to traffic conditions, alternate routes, weather and unforeseen circumstances. Estimated fares do not include road tolls, airport fees or public holiday surcharges.";

        if (ActiveService.FareDetails) {
            fareAmount = GetFareDisplayText(ActiveService.FareDetails, false, props.DoesPgFeeApply);
        }
    }

    const isTemplateModeOn = props.BookingWidgetMode !== BookingWidgetModeKind.Booking;
    
    return (
        <div className="service-info-panel">
            <div className="si-panel-content">
                <div className="si-panel-header">
                    <div className="si-back-section" onClick={CloseInfoView} tabIndex={0} ref={backButtonRef}>
                        <span><img src={getContentUrl(ContentURL.images.arrows.GreyLeftArrow)} className="si-back-btn" alt="Left arrow icon" /></span>
                        <span className="si-back-text">Back</span>
                    </div>                    
                </div>
                <hr className="si-section-divider" />
                <div className="si-service-section">
                    <div className="si-service-header">
                        <div className="si-service-name">{ActiveService.Service.displayName}</div>                        
                        {!isTemplateModeOn && props.IsValidFareDataAvailable &&
                            <div className="si-fare">
                                {priceIconUrl && <img className="si-price-icon" src={priceIconUrl} alt="Currency sign" />}
                                {fareAmount}
                            </div>
                        }
                    </div>
                    <div className="si-short-description">{ActiveService.Service.short}</div>
                    <div className="si-service-image">
                        <img src={ActiveService.Service.image} className="si-image" alt="Image of a vehicle" />
                    </div>
                    {props.ActiveService.RequiresPaxCount && <MaxiSeaterSelector
                        ClickedSeaterNo={NumberOfSeats}
                        MaxiTaxiSeaterInfo={props.MaxiTaxiSeaterInfo}
                        MaxiTaxiLookUp={props.MaxiTaxiLookUp}
                        reportMaxiTaxiSeaterNo={SetMaxiTaxiSeatNo}
                    />}
                </div>
                <div className="si-service-description">
                    <div className="si-service-description-inner">{ActiveService.Service.blurb}</div>
                </div>
                {ActiveService.Service.description && ActiveService.Service.description.trim() !== "" && (
                    <div
                        className="si-service-description-extended"
                        dangerouslySetInnerHTML={{ __html: ActiveService.Service.description }}
                    />
                )}
                <div className="si-service-conditions">
                    <div className="si-fare-condition-title">{fareConditionTitle}<sup>*</sup></div>
                    <div className="si-fare-condition">{fareConditionText}</div>
                </div>
                <div className="si-select-service">
                    <button className="si-select-btn" onClick={SelectCondition} tabIndex={0}>Select This Service</button>
                </div>
            </div>                
        </div>
    );
}

function mapStateToProps(state: ApplicationState): PropsFromStore {
    return {
        ActiveService: state.condition.ActiveService!, // Guaranteed to be not null when this component is mounted.
        ConditionList: state.condition.ConditionList,
        MaxiTaxiLookUp: state.condition.MaxiTaxiLookUp,
        MaxiTaxiSeaterInfo: state.condition.MaxiTaxiSeaterInfo,
        SelectedCondition: state.condition.SelectedCondition,
        IsPriceGuaranteeSelected: state.condition.IsPriceGuaranteeSelected,
        BookingWidgetMode: state.uiLogicControl.BookingForm.BookingWidgetMode,
        IsValidFareDataAvailable: IsValidFareDataAvailable(state),
        IsFixedFareAvailable: IsFixedFareAvailable(state),
        DoesPgFeeApply: OptionalUI.PgFee(state),
    }
}

export default connect(mapStateToProps)(ConditionInfoView);
